import includes from 'lodash/includes';
import React from 'react';
import makeLoadable from '@loadable/component';
import RouterLoader from './RouterLoader';
import {matchRoutes} from 'react-router-config';
import Home from 'page/Home';
import City from 'page/City';
import Legal from 'page/Legal';
import Search from 'page/Search';
import Destination from 'page/Destination';
import MidFunnelAirport from 'page/MidFunnelAirport';
import Category from 'page/Category';
import CatchAll from 'page/CatchAll';
import Checkout from 'page/Checkout';
import SpotDetailsAirport from 'page/SpotDetailsAirport';

export function loadable(module) {
    return makeLoadable(module, {fallback: <RouterLoader />});
}

const routes = [
    {
        path: '/',
        exact: true,
        component: Home,
    },
    {
        path: '/developers',
        component: loadable(() => import('developers/Developers')),
        exact: true,
    },
    {
        path: '/legal/:slug',
        component: Legal,
        exact: true,
    },
    {
        path: '/city/monthly/:slug?',
        component: City,
        exact: true,
    },
    {
        path: '/city/:slug?',
        component: City,
        exact: true,
    },
    {
        path: '/destination/:city?/:slug?',
        component: Destination,
        exact: true,
        isSearch: true,
    },
    {
        path: '/destination/monthly/:city?/:slug?',
        component: Destination,
        exact: true,
        isSearch: true,
    },
    {
        path: '/airport/:city?/:slug?',
        component: MidFunnelAirport,
        exact: true,
    },
    {
        path: '/category/:city?/:slug?',
        component: Category,
        exact: true,
    },
    {
        path: '/checkout/:spotId/:facilitySlug',
        component: Checkout,
    },
    {
        path: '/airport-parking/:spotId/:facilitySlug',
        component: SpotDetailsAirport,
    },
    {
        path: '/search',
        component: Search,
        isSearch: true,
    },
    // TODO: post-search-v2-remove
    ...(process.env.NODE_ENV === 'development'
        ? [
              {
                  path: '/search-v2',
                  component: loadable(() =>
                      import('search-v2/_for-testing/SearchTester')
                  ),
              },
          ]
        : []),
    // the CatchAll route below should always be the last route defined
    // NOTE: because isSearch is true for CatchAll, Search.jsx determines the component to be used instead of CatchAll.jsx
    // look at getPath() in Navigator.jsx to see the conditional
    // however CatchAll.getInitialState() is called to determine what data is loaded
    {
        path: '/:page/:section?',
        component: CatchAll,
        isSearch: true,
    },
];

export function matchPathToRoute(path) {
    const matches = matchRoutes(routes, path);

    if (!matches || matches.length === 0) {
        return null;
    }

    const [
        {
            match: {params},
            route,
        },
    ] = matches;

    return {...route, params};
}

export function isSearchPage(page, section) {
    return (
        page === 'search' ||
        includes(page, 'parking-near-me') ||
        includes(section, 'parking-near-me') ||
        (includes(page, '-parking') && !section) ||
        (!includes(page, '-parking') && includes(section, '-parking')) ||
        includes(page, '-monthly-parking')
    );
}

const possibleBasePages = routes
    .filter(route => !route.path.includes(':page'))
    .map(route => route.path.split('/')[1]);

const excludedRoutes = ['logout', 'favicon.ico'];

/**
 * Checks whether the provided path is a valid route on the server that requires loading of APIs and other page setup functionality.
 * This is necessary so that certain initial load paths (like the excluded routes aboveor error routes) don't overload the server with
 * unnecessary API calls. This method cannot check against citySlugs like the handleRoute method does above because we won't have that
 * information on the server to check against (loading it in an API call is not feasible since the city data will potentially become
 * stale if the node server isn't restarted for a long time and re-makes the API call again to get new city slugs).
 * @function isValidRoute
 * @param {String} path - The requested path.
 * @example
 * isValidRoute('/'); // true
 * isValidRoute('/chicago/ohare-airport-parking'); // true
 * isValidRoute('/logout'); // false
 * @returns {Boolean} - Whether the path is a valid route.
 */
export function isValidRoute(path) {
    const [, page, section] = path.split('/');

    return (
        path === '/' || // homepage
        includes(possibleBasePages, page) || // a base route match (/checkout/*, /airport-parking/*, etc)
        isSearchPage(page, section) || // a search page (excluding redirects like /[city] -> /[city]-parking)
        !includes(excludedRoutes, page) // this MUST be the last condition checked
    );
}

export default routes;
